import { Component, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DateRange, DateRangeOptions, HcSort, HcTableDataSource, PaginationComponent, PresetItem } from "@healthcatalyst/cashmere";
import { Subject, takeUntil } from "rxjs";
import { ChangeElement, ChangeService } from "src/app/shared/services/change.service";
import { ProductLineService } from "src/app/shared/services/product-line.service";
import { SLAService } from "src/app/shared/services/sla.service";
import { StatusService } from "src/app/shared/services/status.service";
import { TypeService } from "src/app/shared/services/type.service";

/** Full change log */
@Component({
    selector: 'pd-admin-full-change-log',
    templateUrl: './admin-full-change-log.component.html',
    styleUrls: ['./admin-full-change-log.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class AdminFullChangeLogComponent {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<ChangeElement> = new HcTableDataSource();
    changeList: any[] = [];
    loading = true;
    loadError = false;
    manyFields = ['Success Stories', 'Images', 'Included Technology', 'Associated Services', 'Previous Names'];
    htmlFields = ['Tagline', 'Problem', 'Approach', 'Benefits and Features', 'Technical Description', 'Case Studies'];
    idFields = ['Type', 'Product Line', 'Status', 'SLA'];
    displayedColumns: string[] = ['date', 'item-id', 'name', 'user', 'field', 'previous', 'change'];
    pageSize: number = 10;
    typeKey: { [key: string]: string } = {};
    productLineKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};
    slaKey: { [key: string]: string } = {};
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    productFilters: string[] = [];
    productText = 'View All';
    now = new Date();
    range: DateRange = { fromDate: new Date(this.now.setDate(this.now.getDate() - 30)), toDate: new Date() };
    selected: number | DateRange = this.range;
    options: DateRangeOptions;
    presets: Array<PresetItem> = [];
    presetSelection = 'None';
    rangeProvided = false;

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get listLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private changeService: ChangeService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private slaService: SLAService,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.loading = true;
        this.typeService.allTypeKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.typeKey = list;
                if (this.productLineKey && this.statusKey && this.slaKey) {
                    this.loading = false;
                }
            } else {
                this.typeService.loadAllTypes();
            }
        });

        this.productLineService.productLineKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLineKey = list;
                this.loading = false;
                if (this.typeKey && this.statusKey && this.slaKey) {
                    this.loading = false;
                }
            } else {
                this.productLineService.refresh();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey && this.productLineKey && this.slaKey) {
                    this.loading = false;
                }
            } else {
                this.statusService.refresh();
            }
        });

        this.slaService.slaKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.slaKey = list;
                if (this.typeKey && this.productLineKey && this.statusKey) {
                    this.loading = false;
                }
            } else {
                this.slaService.refresh();
            }
        });

        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a date range is supplied, update the date range filter
            if (params['fromDate'] && params['toDate']) {
                this.rangeProvided = true;
                this.range = { fromDate: new Date(params['fromDate']), toDate: new Date(params['toDate']) };
                this.selected = this.range;
            }

            this.loadChanges();
            this.filterChanges();
        });

        const today = new Date();
        const fromMin = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        const fromMax = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const toMin = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const toMax = new Date(today.getFullYear(), today.getMonth() + 2, 1);

        this.setupPresets();
        this.options = {
            presets: this.presets,
            format: 'mediumDate',
            applyLabel: 'Apply',
            fromMinMax: { fromDate: fromMin, toDate: fromMax },
            toMinMax: { fromDate: toMin, toDate: toMax },
            startDateIsRequired: true,
            endDateIsRequired: true
        };
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadChanges(): void {
        this.changeService
            .load({
                categories: "technology,service,partner,suite,source"
            })
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    let loadedChanges: ChangeElement[] = [];
                    //display changes with most recent change first
                    data.reverse().forEach((change: any) => {
                        if ( change.update === null ) {
                            change.update = "";
                        }
                        if ( change.previous === null ) {
                            change.previous = "";
                        }
                        loadedChanges.push(change);
                    });
                    this.dataSource = new HcTableDataSource(loadedChanges);
                    this.changeList = loadedChanges;
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.filterChanges();

                    this.loadError = false;
                    this.loading = false;
                },
                error: msg => {
                    this.loadError = true;
                    this.loading = false;
                    console.log(msg);
                }
            });
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'item':
                if (filterArray.length == 1) this.productText = filterArray[0];
                else if (filterArray.length > 1) this.productText = `${filterArray.length} selected`;
                else this.productText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        for (let i = 0; i < this.changeList.length; i++) {
            if (this.changeList[i][type]) {
                if (!filterData.includes(this.changeList[i][type])) {
                    filterData.push(this.changeList[i][type]);
                }
            }
        }

        this.filterData = filterData;
    }

    filterChanges() {
        let filteredChanges: ChangeElement[] = [];

        for (let i = 0; i < this.changeList.length; i++) {
            let productFilterMatch = this.productFilters.length === 0;
            let dateMatch = false;

            if (this.productFilters.includes(this.changeList[i]['item'])) {
                productFilterMatch = true;
            }

            if (typeof(this.selected) === 'object') {
                const fromDate = new Date(this.selected.fromDate!);
                const toDate = new Date(this.selected.toDate!);

                if ((new Date(this.changeList[i]['date']) >= fromDate) && (new Date(this.changeList[i]['date']) <= toDate)) {
                    dateMatch = true
                };
            } else if (typeof(this.selected) === 'number') {
                const fromDate = new Date (this.presets[this.selected].range.fromDate!);
                const toDate = new Date (this.presets[this.selected].range.toDate!);

                if ((new Date(this.changeList[i]['date']) >= fromDate) && (new Date(this.changeList[i]['date']) <= toDate)) {
                    dateMatch = true
                };
            }

            if (productFilterMatch && dateMatch) {
                filteredChanges.push(this.changeList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered partner list
        this.dataSource = new HcTableDataSource(filteredChanges);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);

        if (this.filterType === 'item') {
            this.productFilters = filterArray;
        }

        this.filterChanges();
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === "Type") {
            reformattedID = this.typeKey[id.toString()];
        } else if (type === "Product Line") {
            reformattedID = this.productLineKey[id.toString()];
        } else if (type === 'Status') {
            reformattedID = this.statusKey[id.toString()];
        } else if (type === 'SLA') {
            reformattedID = this.slaKey[id.toString()];
        }

        return reformattedID
    }

    //create and object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    ////daterange picker functions
    updateRange(range: DateRange): void {
        this.range = range;
        this.filterChanges();
    }

    updatePreset(index: number | DateRange): void {
        if (typeof index === 'number') {
            this.presetSelection = this.presets[index].presetLabel;
            this.selected = index;
        } else {
            this.presetSelection = 'None';
            this.selected = this.range;
        }
        this.filterChanges();
    }

    setupPresets(): void {
        const backDate = (numOfDays: number) => {
            const now = new Date();
            return new Date(now.setDate(now.getDate() - numOfDays));
        };

        const today = new Date();
        const yesterday = backDate(1);
        const minus7 = backDate(7);
        const minus30 = backDate(30);
        const currMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const currMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

        this.presets = [
            {
                presetLabel: 'Yesterday',
                range: { fromDate: yesterday, toDate: today }
            },
            {
                presetLabel: 'Last 7 days',
                range: { fromDate: minus7, toDate: today }
            },
            {
                presetLabel: 'Last 30 days',
                range: { fromDate: minus30, toDate: today }
            },
            {
                presetLabel: 'This month',
                range: { fromDate: currMonthStart, toDate: currMonthEnd }
            },
            {
                presetLabel: '1 month to end',
                range: { fromDate: currMonthStart, toDate: currMonthEnd }
            },
            {
                presetLabel: 'Last month',
                range: { fromDate: lastMonthStart, toDate: lastMonthEnd }
            }
        ];
    }

}

